import { CartLinesUpdateEvent } from '@shopify/events';

// Même clé favoris que les autres sections.
const WISHLIST_KEY = 'cpc:wishlist';

/** @returns {Set<string>} */
function readWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

/** @param {Set<string>} ids */
function writeWishlist(ids) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

/** @param {number} cents */
function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const symbol = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  return symbol ? `${amount} ${symbol}` : amount;
}

/**
 * Grille collection bento. Tout est en événements DÉLÉGUÉS pour survivre au
 * re-render des filtres (morph du shopify-section) et à la pagination : favoris,
 * quick add via drawer de tailles, et le drawer lui-même.
 */
class Collection2Grid extends HTMLElement {
  #abort = new AbortController();
  /** @type {MutationObserver | null} */ #observer = null;

  /** @type {any} */ #product = null;
  /** @type {(string|null)[]} */ #selected = [];
  /** @type {any} */ #variant = null;

  connectedCallback() {
    const { signal } = this.#abort;
    this.drawer = this.querySelector('[data-cfs-drawer]');

    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.drawer && !this.drawer.hidden) this.#closeDrawer();
      },
      { signal }
    );

    this.#applyWishlistState();
    // Les cartes changent (filtres/pagination) : on réapplique l'état favoris.
    this.#observer = new MutationObserver(() => this.#applyWishlistState());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
  }

  /* ---------- Délégation ---------- */

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const optValue = target.closest('.cfs__opt-value');
    if (optValue && this.contains(optValue)) {
      this.#selectValue(Number(optValue.dataset.index), optValue.dataset.value);
      return;
    }

    if (target.closest('[data-cfs-close]')) {
      this.#closeDrawer();
      return;
    }

    const addBtn = target.closest('.cfs__drawer-add');
    if (addBtn) {
      if (this.#variant) this.#addToCart(String(this.#variant.id), () => this.#closeDrawer());
      return;
    }

    const quickAdd = target.closest('.cfs__quick-add');
    if (quickAdd && this.contains(quickAdd)) {
      event.preventDefault();
      event.stopPropagation();
      if (Number(quickAdd.dataset.variantCount) <= 1) {
        this.#addToCart(quickAdd.dataset.variantId);
      } else {
        this.#openDrawer(quickAdd.dataset.productUrl);
      }
      return;
    }

    const wishlist = target.closest('.cfs__wishlist');
    if (wishlist && this.contains(wishlist)) {
      event.preventDefault();
      event.stopPropagation();
      this.#toggleWishlist(/** @type {HTMLElement} */ (wishlist));
    }
  };

  /* ---------- Favoris ---------- */

  #applyWishlistState() {
    const saved = readWishlist();
    for (const button of this.querySelectorAll('.cfs__wishlist')) {
      const id = button.dataset.productId;
      button.setAttribute('aria-pressed', String(Boolean(id && saved.has(id))));
    }
  }

  #toggleWishlist(button) {
    const id = button.dataset.productId;
    if (!id) return;
    const ids = readWishlist();
    const wasSaved = ids.has(id);
    if (wasSaved) ids.delete(id);
    else ids.add(id);
    writeWishlist(ids);
    button.setAttribute('aria-pressed', String(!wasSaved));
  }

  /* ---------- Drawer tailles ---------- */

  async #openDrawer(url) {
    if (!this.drawer || !url) return;
    try {
      const product = await fetch(`${url}.js`).then((r) => r.json());
      this.#product = product;
      this.#selected = product.options.map(() => null);
      this.#variant = null;
      this.#renderDrawer();
      this.#showDrawer();
    } catch (error) {
      console.error('[collection2] chargement produit impossible', error);
    }
  }

  #renderDrawer() {
    const product = this.#product;
    this.drawer.querySelector('.cfs__drawer-title').textContent = product.title;
    this.drawer.querySelector('.cfs__drawer-price').textContent = formatMoney(product.price);

    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    const optionsEl = this.drawer.querySelector('.cfs__drawer-options');
    optionsEl.textContent = '';

    names.forEach((name, index) => {
      const values = [...new Set(product.variants.map((v) => v.options[index]))];
      const group = document.createElement('div');
      group.className = 'cfs__opt';
      const label = document.createElement('span');
      label.className = 'cfs__opt-name';
      label.textContent = name;
      const row = document.createElement('div');
      row.className = 'cfs__opt-values';
      values.forEach((value) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cfs__opt-value';
        btn.textContent = value;
        btn.dataset.index = String(index);
        btn.dataset.value = value;
        btn.setAttribute('aria-pressed', 'false');
        row.appendChild(btn);
      });
      group.append(label, row);
      optionsEl.appendChild(group);
    });

    this.#refreshOptionStates();
    this.#updateVariant();
  }

  #selectValue(index, value) {
    this.#selected[index] = this.#selected[index] === value ? null : value;
    this.#refreshOptionStates();
    this.#updateVariant();
  }

  #refreshOptionStates() {
    const product = this.#product;
    for (const btn of this.drawer.querySelectorAll('.cfs__opt-value')) {
      const index = Number(btn.dataset.index);
      const value = btn.dataset.value;
      btn.setAttribute('aria-pressed', String(this.#selected[index] === value));
      btn.disabled = !product.variants.some((v) => {
        if (!v.available) return false;
        if (v.options[index] !== value) return false;
        return this.#selected.every((sel, i) => i === index || sel === null || v.options[i] === sel);
      });
    }
  }

  #updateVariant() {
    const product = this.#product;
    const addBtn = this.drawer.querySelector('.cfs__drawer-add');
    const complete = this.#selected.every((s) => s !== null);
    this.#variant = complete
      ? product.variants.find((v) => this.#selected.every((sel, i) => v.options[i] === sel))
      : null;
    if (this.#variant) {
      this.drawer.querySelector('.cfs__drawer-price').textContent = formatMoney(this.#variant.price);
    }
    const ok = Boolean(this.#variant && this.#variant.available);
    addBtn.disabled = !ok;
    addBtn.textContent = ok ? this.dataset.addLabel || 'Ajouter au panier' : this.dataset.chooseLabel || 'Choisir';
  }

  #showDrawer() {
    this.drawer.hidden = false;
    void this.drawer.offsetWidth;
    this.drawer.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }

  #closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    const panel = this.drawer.querySelector('.cfs__drawer-panel');
    const done = () => {
      this.drawer.hidden = true;
      panel.removeEventListener('transitionend', done);
    };
    panel.addEventListener('transitionend', done);
  }

  /* ---------- Panier ---------- */

  async #addToCart(variantId, onDone) {
    if (!variantId) return;
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      }).then((r) => r.json());
      if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'collection2', sourceId: this.id, itemCount: 1, didError: false },
      });
      if (onDone) onDone();
    } catch (error) {
      console.error('[collection2] ajout au panier impossible', error);
      deferred.reject(error);
    }
  }
}

if (!customElements.get('collection2-grid')) {
  customElements.define('collection2-grid', Collection2Grid);
}
